import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Bell, Plus, Trash2, Calendar } from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PersonalReminders = ({ user }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newReminder, setNewReminder] = useState({
    text: '',
    due_date: ''
  });

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!newReminder.text.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/reminders`, newReminder, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reminder added');
      fetchReminders();
      setIsAddOpen(false);
      setNewReminder({ text: '', due_date: '' });
    } catch (error) {
      toast.error('Failed to add reminder');
    }
  };

  const toggleComplete = async (reminder) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/reminders/${reminder.id}`, 
        { is_completed: !reminder.is_completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchReminders();
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const deleteReminder = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/reminders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reminder deleted');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const getDueDateClass = (dueDate, isCompleted) => {
    if (isCompleted) return 'text-slate-400';
    if (!dueDate) return 'text-slate-500';
    const date = new Date(dueDate);
    if (isPast(date) && !isToday(date)) return 'text-red-600 font-medium';
    if (isToday(date)) return 'text-orange-600 font-medium';
    return 'text-slate-500';
  };

  const activeReminders = reminders.filter(r => !r.is_completed);
  const completedReminders = reminders.filter(r => r.is_completed);

  if (loading) {
    return (
      <div className="animate-pulse bg-slate-100 rounded-lg h-32"></div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">My Reminders</h3>
          <span className="text-xs text-slate-400">(Only you can see these)</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => setIsAddOpen(true)} data-testid="add-reminder-btn">
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {reminders.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">
          No reminders yet. Add one to keep track of personal notes.
        </p>
      ) : (
        <div className="space-y-4">
          {/* Active Reminders */}
          {activeReminders.length > 0 && (
            <div className="space-y-2">
              {activeReminders.map(reminder => (
                <div 
                  key={reminder.id} 
                  className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded-lg group"
                  data-testid={`reminder-${reminder.id}`}
                >
                  <Checkbox
                    checked={reminder.is_completed}
                    onCheckedChange={() => toggleComplete(reminder)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{reminder.text}</p>
                    {reminder.due_date && (
                      <p className={`text-xs mt-0.5 flex items-center gap-1 ${getDueDateClass(reminder.due_date, reminder.is_completed)}`}>
                        <Calendar className="h-3 w-3" />
                        {format(new Date(reminder.due_date), 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteReminder(reminder.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Completed Reminders */}
          {completedReminders.length > 0 && (
            <div className="border-t pt-3 mt-3">
              <p className="text-xs text-slate-400 mb-2">Completed</p>
              <div className="space-y-1">
                {completedReminders.map(reminder => (
                  <div 
                    key={reminder.id} 
                    className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded-lg group opacity-60"
                  >
                    <Checkbox
                      checked={reminder.is_completed}
                      onCheckedChange={() => toggleComplete(reminder)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm line-through">{reminder.text}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteReminder(reminder.id)}
                      className="opacity-0 group-hover:opacity-100 text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Reminder Modal */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Personal Reminder</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddReminder} className="space-y-4">
            <div>
              <Input
                placeholder="What do you want to remember?"
                value={newReminder.text}
                onChange={(e) => setNewReminder({ ...newReminder, text: e.target.value })}
                required
                autoFocus
                data-testid="reminder-text-input"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600 block mb-1">Due date (optional)</label>
              <Input
                type="date"
                value={newReminder.due_date}
                onChange={(e) => setNewReminder({ ...newReminder, due_date: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-reminder-btn">
                Add Reminder
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonalReminders;
